import { useCallback } from "react";
import { useLocation } from "wouter";

/** O que gravamos no `history.state` ao abrir um detalhe a partir de uma lista. */
export type EstadoNav = { voltarPara?: string } | null;

/** A URL desta entrada do histórico, com a query string — é ela que carrega os filtros. */
export function urlAtual(): string {
  if (typeof window === "undefined") return "/";
  return window.location.pathname + window.location.search;
}

/** A origem gravada por `abrir()`, se a entrada anterior do histórico for nossa. */
export function origemDoHistorico(state: unknown): string | undefined {
  const s = state as EstadoNav;
  return s && typeof s.voltarPara === "string" ? s.voltarPara : undefined;
}

/**
 * Decide o que "Voltar" faz — separado do hook para ser testável sem DOM.
 *
 * `history.back()` só quando a entrada anterior é comprovadamente NOSSA, e é
 * exatamente isso que `voltarPara` atesta: só quem passou por `abrir()` tem essa
 * chave. Sem ela (deep link do WhatsApp, aba nova, reload numa rota sem origem)
 * vai para o fallback com `replace`.
 *
 * Não usar `history.length`: ele conta entradas de outros sites na mesma aba e
 * não diz nada sobre de onde o usuário veio.
 */
export function decidirVoltar(
  state: unknown,
  fallback: string,
): { tipo: "back" } | { tipo: "fallback"; destino: string } {
  return origemDoHistorico(state) ? { tipo: "back" } : { tipo: "fallback", destino: fallback };
}

/**
 * Navegação entre lista ↔ detalhe ↔ formulário. Quatro verbos, um único ponto
 * de verdade — o que impede o "Voltar" de virar `setLocation("/rota-fixa")` de
 * novo, que é o que mandava para `/cacho/vendas` quem tinha aberto a venda a
 * partir de `/mercado/receber`.
 *
 *  - `abrir(destino)`      push gravando a URL atual (com filtros) como origem.
 *  - `voltar(fallback)`    `history.back()` se a entrada anterior é nossa; senão fallback (replace).
 *  - `substituir(destino)` replace PRESERVANDO a origem (pós-criação: /novo → /:id).
 *  - `encerrar(fallback)`  replace para a origem (pós-exclusão: o detalhe morto sai do histórico,
 *                          e um "voltar" seguinte não reabre um 404).
 */
export function useNavegacao() {
  const [, navegar] = useLocation();

  const abrir = useCallback(
    (destino: string) => navegar(destino, { state: { voltarPara: urlAtual() } }),
    [navegar],
  );

  const voltar = useCallback(
    (fallback: string) => {
      const decisao = decidirVoltar(window.history.state, fallback);
      if (decisao.tipo === "back") window.history.back();
      else navegar(decisao.destino, { replace: true });
    },
    [navegar],
  );

  const substituir = useCallback(
    (destino: string) => navegar(destino, { replace: true, state: window.history.state }),
    [navegar],
  );

  const encerrar = useCallback(
    (fallback: string) =>
      navegar(origemDoHistorico(window.history.state) ?? fallback, { replace: true }),
    [navegar],
  );

  return { abrir, voltar, substituir, encerrar };
}

/**
 * Para `<Link href=… state={origemAtual()}>` do Wouter, quando a navegação já é
 * um `<Link>` e transformá-la em `onClick` custaria o comportamento de
 * ctrl+clique. Em linha/card clicável, preferir `abrir()`.
 */
export function origemAtual(): { voltarPara: string } {
  return { voltarPara: urlAtual() };
}
